import { useEffect, useMemo, useRef, useState } from 'react';
import type { CheckState, TreeNode } from '../../domain/tree';
import { collectLeaves, filterTree, nodeState, toggleNode } from '../../domain/tree';
import { Checkbox } from '../form';
import { SearchInput } from '../form';
import { Button } from '../form';

export function TreeSelect({
  nodes,
  selected,
  onChange,
  readOnly,
  defaultExpanded = true,
  highlight,
}: {
  nodes: TreeNode[];
  selected: ReadonlySet<string>;
  onChange?: (next: Set<string>) => void;
  readOnly?: boolean;
  defaultExpanded?: boolean;
  highlight?: ReadonlySet<string>;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(() =>
    defaultExpanded ? new Set(collectBranchIds(nodes)) : new Set(),
  );
  const seeded = useRef(Boolean(nodes.length) || !defaultExpanded);
  const visible = useMemo(() => filterTree(nodes, query), [nodes, query]);

  useEffect(() => {
    if (!defaultExpanded || seeded.current || !nodes.length) return;
    setOpen(new Set(collectBranchIds(nodes)));
    seeded.current = true;
  }, [nodes, defaultExpanded]);

  useEffect(() => {
    if (!query) return;
    const next = new Set<string>();
    const mark = (list: TreeNode[]): void => {
      for (const node of list) {
        if (node.children?.length) {
          next.add(node.id);
          mark(node.children);
        }
      }
    };
    mark(visible);
    setOpen(next);
  }, [query, visible]);

  const render = (list: TreeNode[], depth: number): JSX.Element[] =>
    list.map((node) => {
      const state: CheckState = nodeState(node, selected);
      const hasChildren = Boolean(node.children?.length);
      const expanded = open.has(node.id);
      const count = hasChildren ? collectLeaves(node.children ?? []) : [];
      const on = count.filter((id) => selected.has(id)).length;
      return (
        <div key={node.id}>
          <div
            className={`tbm-tree__row${node.disabled ? ' is-disabled' : ''}${highlight?.has(node.id) ? ' is-focus' : ''}`}
            role="treeitem"
            title={node.hint}
            aria-expanded={hasChildren ? expanded : undefined}
            aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
            data-depth={depth}
            onClick={() => {
              if (hasChildren) {
                setOpen((cur) => {
                  const n = new Set(cur);
                  if (n.has(node.id)) n.delete(node.id);
                  else n.add(node.id);
                  return n;
                });
              }
            }}
          >
            {hasChildren ? (
              <button type="button" className="tbm-tree__toggle" aria-label={expanded ? 'Replier' : 'Déplier'}>
                {expanded ? '▾' : '▸'}
              </button>
            ) : (
              <span className="tbm-tree__toggle" />
            )}
            <Checkbox
              state={state}
              onToggle={
                readOnly || node.disabled
                  ? undefined
                  : () => onChange?.(toggleNode(nodes, selected, node.id))
              }
            />
            <span>{node.label}</span>
            {hasChildren ? <span className="tbm-tree__count">{on}/{count.length}</span> : null}
          </div>
          {hasChildren && expanded ? render(node.children ?? [], depth + 1) : null}
        </div>
      );
    });

  return (
    <div className="tbm-tree-wrap">
      <div className="tbm-toolbar tbm-tree__toolbar">
        <SearchInput value={query} onChange={setQuery} placeholder="Filtrer l’arbre" />
        <Button size="sm" variant="ghost" onClick={() => setOpen(new Set(collectBranchIds(nodes)))}>Tout déplier</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(new Set())}>Tout replier</Button>
      </div>
      <div className="tbm-tree" role="tree">
        {render(visible, 0)}
      </div>
    </div>
  );
}

function collectBranchIds(nodes: TreeNode[]): string[] {
  const out: string[] = [];
  const walk = (list: TreeNode[]): void => {
    for (const node of list) {
      if (node.children?.length) {
        out.push(node.id);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return out;
}
